'use client';

import {
  cn,
  type createStyleType,
  inputStyles,
  inputThemeableVars,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ctct/summit-core';
import { useSummitI18n } from '@ctct/summit-core';
import { CheckIcon } from '@ctct/summit-icons';
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
  useCallback,
  useMemo,
  useRef,
  type WheelEventHandler,
} from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../Command';
import { ComboboxTrigger } from './ComboboxTrigger';

const themeableVars = [
  ...inputThemeableVars.base,
  ...inputThemeableVars.focus,
  ...inputThemeableVars.textfield,
  ...inputThemeableVars.error,
  'r:[--item-bg-selected:theme(selectItem.bg.selected)]',
  'r:[--item-text-selected:theme(selectItem.text.selected)]',
  'r:[--icon-selected:theme(switch.bg.selected)]',
] as const;
type ComboboxStyle = ReturnType<typeof createStyleType<typeof themeableVars>>;

interface Item {
  value: string;
  label: string;
}

type GroupedItems = Record<string, Item[]>;
type SelectedItems = string | string[];

interface IconStyle extends CSSProperties {
  ['--icon-selected']?: string;
}

const selectedIconSize = 'r:h-6 r:w-6';

const SelectedIcon = ({ isSelected, style }: { isSelected: boolean; style?: IconStyle }) => (
  <CheckIcon
    className={cn(
      'rds-combobox-selected-icon',
      selectedIconSize,
      'r:text-(--icon-selected)',
      isSelected ? 'r:opacity-100' : 'r:opacity-0'
    )}
    style={style}
  />
);

interface ComboboxTriggerProps {
  isOpen: boolean;
  disabled?: boolean;
  selected: string[];
  selectedLabels: string;
  hasError?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- we need a discriminating union
type ComboboxBaseProps = {
  /** Selectable options */
  items: Item[] | GroupedItems;
  /** The selected item or items */
  selected: SelectedItems;
  /** Disables the combobox from interaction */
  disabled?: boolean;
  /** Allow multiple selections to be made */
  isMultiSelect?: boolean;
  /** Indicate whether the combobox popover is open or not */
  isOpen?: boolean;
  /** When set to true, interaction with outside elements will be disabled and only dialog content will be visible to screen readers. */
  isModal?: boolean;
  /** Callback when the open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Callback when combobox is blurred */
  onBlur?: (e: FocusEvent) => void;
  /** Callback when key is pressed */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /** Callback to handle custom filtering on search input */
  onSearch?: (value: string) => void;
  /** Custom render function for the trigger button */
  renderTrigger?: (props: ComboboxTriggerProps) => ReactNode;
  /** Custom render function for each item */
  renderItem?: (item: Item) => ReactNode;
  /** Optional footer */
  footer?: ReactNode;
  /** Text to display on the trigger button when no items are selected */
  buttonText?: string;
  /** Placeholder text for the search input */
  placeholderText?: string;
  /** Text to display when no items match the search */
  emptyText?: string;
  /** Additional CSS classes for the combobox */
  className?: string;
  /** Additional CSS classes for the trigger button */
  triggerClasses?: string;
  /** Additional CSS classes for the content area */
  contentClasses?: string;
  /** Custom portal container for the popover */
  portalContainer?: HTMLElement | null | false;
  /** Set inline styles, including the component's built-in CSS variables. */
  style?: ComboboxStyle;
  /** Additional props for the CommandInput */
  inputProps?: ComponentPropsWithoutRef<typeof CommandInput>;
  /** Whether the combobox has an error state */
  hasError?: boolean;
  /** Ref to the combobox trigger button */
  ref?: Ref<HTMLButtonElement>;
};

type SingleSelectHandler = (value: string) => void;
type MultiSelectHandler = (value: string[]) => void;

type ComboboxSingleProps = ComboboxBaseProps & {
  isMultiSelect?: false;
  onChange?: SingleSelectHandler;
};

type ComboboxMultiProps = ComboboxBaseProps & {
  isMultiSelect: true;
  onChange?: MultiSelectHandler;
};

type ComboboxProps = ComboboxSingleProps | ComboboxMultiProps;

const DEFAULT_TEXTS = {
  buttonText: 'combobox.defaultButtonText',
  placeholderText: 'combobox.defaultPlaceholder',
  emptyText: 'combobox.defaultEmptyText',
} as const;

const Combobox = ({
  items,
  selected,
  disabled,
  isMultiSelect = false,
  isOpen,
  isModal = true,
  onOpenChange: handleOpenChange,
  onChange,
  onSearch: handleSearch,
  onBlur: handleBlur,
  onKeyDown: handleKeyDown,
  renderTrigger,
  renderItem,
  footer,
  buttonText,
  placeholderText,
  emptyText,
  triggerClasses,
  contentClasses,
  portalContainer,
  style,
  inputProps,
  hasError,
  ref,
}: ComboboxProps) => {
  const { tExtended } = useSummitI18n();
  const isUngrouped = Array.isArray(items);
  const standardizedItems = useMemo(() => {
    if (isUngrouped) return { default: items };
    return items;
  }, [items, isUngrouped]);

  const selectedItems = useMemo(() => {
    if (Array.isArray(selected)) return selected;
    if (selected) return [selected];
    return [];
  }, [selected]);

  const buttonStyles = `
    ${themeableVars.join(' ')}
    r:px-3 r:w-52 r:justify-between
    r:text-base
    r:text-(--text)
    r:disabled:bg-(--bg-disabled)
    r:disabled:cursor-not-allowed
    r:border-(--border)
    r:focus-visible:outline-none
    r:focus-visible:ring-4
    r:focus-visible:border-(--border-active))]
    r:focus-visible:ring-(--focus)
    r:[&>span]:leading-5
    r:[--bg-hover:var(--sds-input-bg-default)]
    ${hasError ? inputStyles.error : ''}
  `;

  const contentStyles = `
    ${themeableVars.join(' ')}
    r:w-(--radix-popover-trigger-width)
    r:max-h-(--radix-popover-content-available-height)
    r:p-0
  `;

  const handleSelect = useCallback(
    (selectedValue: string) => {
      if (!onChange) return;

      if (isMultiSelect) {
        const currentSelection = Array.isArray(selected) ? selected : [];
        const isAlreadySelected = currentSelection.includes(selectedValue);

        const updatedSelection = isAlreadySelected
          ? currentSelection.filter((item) => item !== selectedValue)
          : [...currentSelection, selectedValue];

        (onChange as MultiSelectHandler)(updatedSelection);
      } else {
        (onChange as SingleSelectHandler)(selectedValue);
      }
    },
    [isMultiSelect, selected, onChange]
  );

  const findLabelByValue = useCallback(
    (value: string) => {
      return (
        Object.values(standardizedItems)
          .flat()
          .find((item) => item.value === value)?.label ?? ''
      );
    },
    [standardizedItems]
  );

  const selectedLabels = useMemo(() => {
    return selectedItems.map(findLabelByValue).filter(Boolean).join(', ');
  }, [selectedItems, findLabelByValue]);

  const flatItems = useMemo(() => Object.values(standardizedItems).flat(), [standardizedItems]);

  const defaultButtonText = tExtended(DEFAULT_TEXTS.buttonText);
  const defaultPlaceholder = tExtended(DEFAULT_TEXTS.placeholderText);
  const defaultEmptyText = tExtended(DEFAULT_TEXTS.emptyText);

  const Trigger = renderTrigger ? (
    renderTrigger({
      isOpen: !!isOpen,
      disabled,
      selected: selectedItems,
      selectedLabels,
      ref,
      ...(hasError ? { hasError: true } : {}),
    })
  ) : (
    <ComboboxTrigger
      isOpen={!!isOpen}
      disabled={disabled}
      selected={selectedItems}
      selectedLabels={selectedLabels}
      buttonText={buttonText ?? defaultButtonText}
      style={style}
      triggerClasses={cn(buttonStyles, triggerClasses)}
      onSelect={handleSelect}
      isMultiSelect={isMultiSelect}
      items={flatItems}
      ref={ref}
    />
  );

  const commandListRef = useRef<HTMLDivElement>(null);

  const handleWheel: WheelEventHandler = (e) => {
    e.stopPropagation();
    if (commandListRef.current) commandListRef.current.scrollTop += e.deltaY;
  };

  const handleOnValueChange = (value: string) => {
    if (handleSearch) handleSearch(value);
    if (commandListRef.current) commandListRef.current.scrollTop = 0;
  };

  return (
    <Popover modal={isModal} open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{Trigger}</PopoverTrigger>
      <PopoverContent
        className={cn('rds-combobox-content r:flex r:flex-col', contentStyles, contentClasses)}
        container={portalContainer === false ? null : portalContainer}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      >
        <Command
          shouldFilter={!handleSearch}
          className="r:flex r:flex-col r:flex-1"
          style={{
            '--item-bg-selected': style?.['--item-bg-selected'],
            '--item-text-selected': style?.['--item-text-selected'],
            '--text': style?.['--text'],
          }}
        >
          <CommandInput
            onValueChange={handleOnValueChange}
            placeholder={placeholderText ?? defaultPlaceholder}
            {...inputProps}
          />
          <CommandList
            ref={commandListRef}
            onWheel={handleWheel}
            className={cn('r:flex-1 r:min-h-0 r:overflow-y-auto')}
          >
            <CommandEmpty>{emptyText ?? defaultEmptyText}</CommandEmpty>
            {Object.entries(standardizedItems).map(([group, groupItems]) => (
              <CommandGroup
                heading={!isUngrouped && group}
                key={group}
                className={cn(
                  'rds-combobox-group',
                  `
                    r:[&+&]:-mt-2
                    r:[&_[cmdk-group-heading]]:uppercase
                    r:[&_[cmdk-group-heading]]:font-semibold
                  `
                )}
              >
                {groupItems.map((item) => {
                  const isSelected = selectedItems.includes(item.value);
                  return (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      keywords={[item.label]}
                      tabIndex={0}
                      onSelect={() => handleSelect(item.value)}
                      className={cn(
                        'rds-combobox-item',
                        'r:mb-1 r:gap-2 r:focus-visible:ring-4 r:focus-visible:ring-(--focus)',
                        isSelected && 'r:bg-(--item-bg-selected) r:text-(--item-text-selected)'
                      )}
                    >
                      {renderItem ? renderItem(item) : item.label}
                      <div
                        className={cn(
                          'rds-combobox-selected-icon-container',
                          selectedIconSize,
                          'r:flex r:ml-auto'
                        )}
                      >
                        <SelectedIcon
                          isSelected={isSelected}
                          style={{ '--icon-selected': style?.['--icon-selected'] }}
                        />
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
          {footer && (
            <div className="r:py-2 r:px-3 r:border-t r:border-neutral-300 r:w-full r:flex r:flex-none">
              {footer}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export {
  Combobox,
  type Item as ComboboxItem,
  type ComboboxMultiProps,
  type MultiSelectHandler as ComboboxMultiSelectHandler,
  type ComboboxProps,
  type SelectedItems as ComboboxSelectedItems,
  type ComboboxSingleProps,
  type SingleSelectHandler as ComboboxSingleSelectHandler,
  type ComboboxTriggerProps,
};
